import { memo, useCallback } from 'react'
import {
  Box,
  TextField,
  InputAdornment,
  Typography,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material'
import { SxProps, Theme } from '@mui/system'
import ArrowDropUp from '@mui/icons-material/ArrowDropUp'
import ArrowDropDown from '@mui/icons-material/ArrowDropDown'
import { QuestionnaireWithSections } from '../types'
import { SortOrder } from '../hooks/useProcessStatus'
import { Pagination } from './Pagination'
import { IconName } from '../../../../../../../utils/icons'

interface ProcessPerformancePanelProps {
  questionnaires: QuestionnaireWithSections[]
  searchValue: string
  onSearchChange: (value: string) => void
  sortOrder: SortOrder
  onSortOrderChange: (order: SortOrder) => void
  expandedIds: Set<string>
  onToggleQuestionnaire: (id: string) => void
  pageRight: number
  totalPagesRight: number
  onPageChangeRight: (page: number) => void
  loading?: boolean
  styles: {
    processPanel: SxProps<Theme>
    processPanelHeader: SxProps<Theme>
    processPanelTitle: SxProps<Theme>
    searchField: SxProps<Theme>
    rightQuestionnaireRow: SxProps<Theme>
    rightQuestionnaireName: SxProps<Theme>
    rightQuestionnaireBadge: SxProps<Theme>
    rightQuestionnaireArrow: SxProps<Theme>
    rightSectionRow: SxProps<Theme>
    rightSectionLeftColumn: SxProps<Theme>
    rightSectionRightColumn: SxProps<Theme>
    rightSectionIndicator: SxProps<Theme>
    rightSectionName: SxProps<Theme>
    rightSectionTrack: SxProps<Theme>
    rightSectionFill: SxProps<Theme>
    rightSectionScore: SxProps<Theme>
    toggleButtonGroup: SxProps<Theme>
    toggleButton: SxProps<Theme>
    toggleButtonActive: SxProps<Theme>
    processDivider: SxProps<Theme>
    paginationContainer: SxProps<Theme>
    paginationButton: SxProps<Theme>
    paginationPage: SxProps<Theme>
    paginationPageActive: SxProps<Theme>
    paginationDisabled: SxProps<Theme>
  }
  getScoreColor: (score: number) => string
  icons: (name: IconName) => React.ReactNode
  t: (key: string, options?: Record<string, unknown>) => string
}

const ProcessPerformancePanel = ({
  questionnaires,
  searchValue,
  onSearchChange,
  sortOrder,
  onSortOrderChange,
  expandedIds,
  onToggleQuestionnaire,
  pageRight,
  totalPagesRight,
  onPageChangeRight,
  loading = false,
  styles,
  getScoreColor,
  icons,
  t,
}: ProcessPerformancePanelProps) => {
  const handleSortChange = useCallback(
    (_: React.MouseEvent<HTMLElement>, value: SortOrder | null) => {
      if (value !== null) {
        onSortOrderChange(value)
      }
    },
    [onSortOrderChange]
  )

  return (
    <Box sx={styles.processPanel}>
      {/* Header */}
      <Box sx={styles.processPanelHeader}>
        <Typography sx={styles.processPanelTitle}>
          {t('dashboard.storePerformance.processStatus.processPerformance')}
        </Typography>
        <ToggleButtonGroup
          value={sortOrder}
          exclusive
          onChange={handleSortChange}
          sx={{
            ...styles.toggleButtonGroup,
            border: sortOrder === 'asc' ? '2px solid #4a90e2' : '2px solid #22c55e',
            backgroundColor: sortOrder === 'asc' ? '#4a90e2' : '#22c55e',
          }}
        >
          <ToggleButton
            value="desc"
            sx={{
              ...(sortOrder === 'desc' ? styles.toggleButtonActive : styles.toggleButton),
              backgroundColor: sortOrder === 'desc' ? '#fff' : (sortOrder === 'asc' ? '#4a90e2' : '#22c55e'),
              '&.Mui-selected': {
                borderRadius: sortOrder === 'desc' ? '0 20px 20px 0 !important' : undefined,
                backgroundColor: sortOrder === 'desc' ? '#fff !important' : undefined,
                color: sortOrder === 'desc' ? '#1f2937 !important' : undefined,
              },
            }}
          >
            {t('dashboard.storePerformance.processStatus.highestToLowest')}
          </ToggleButton>
          <ToggleButton
            value="asc"
            sx={{
              ...(sortOrder === 'asc' ? styles.toggleButtonActive : styles.toggleButton),
              backgroundColor: sortOrder === 'asc' ? '#fff' : '#22c55e',
              '&.Mui-selected': {
                borderRadius: sortOrder === 'asc' ? '20px 0 0 20px !important' : undefined,
                backgroundColor: sortOrder === 'asc' ? '#fff !important' : undefined,
                color: sortOrder === 'asc' ? '#1f2937 !important' : undefined,
              },
            }}
          >
            {t('dashboard.storePerformance.processStatus.lowestToHighest')}
          </ToggleButton>
        </ToggleButtonGroup>
      </Box>

      {/* Search */}
      <Box sx={{ padding: '26px 14px 16px 16px' }}>
        <TextField
          placeholder={t('dashboard.storePerformance.search')}
          size="small"
          fullWidth
          value={searchValue}
          onChange={(e) => onSearchChange(e.target.value)}
          sx={styles.searchField}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                {icons('search')}
              </InputAdornment>
            ),
          }}
        />
      </Box>

      {/* Scrollable content */}
      <Box sx={{ flex: 1, overflowY: 'auto', minHeight: 0}}>
        {loading ? (
          <Box sx={{ p: 2, textAlign: 'center' }}>
            <Typography sx={{ fontSize: '13px', color: '#9ca3af' }}>
              {t('general.loading')}
            </Typography>
          </Box>
        ) : (
          <>
            {/* Questionnaires list */}
            {questionnaires.map((q) => {
          const isExpanded = expandedIds.has(q.id)
          const badgeColor = getScoreColor(q.score)
          return (
            <Box key={q.id}>
              {/* Questionnaire header (collapsible with badge) */}
              <Box
                sx={isExpanded
                  ? { ...styles.rightQuestionnaireRow, marginBottom: '0', borderRadius: '10px 10px 0 0' }
                  : styles.rightQuestionnaireRow
                }
                onClick={() => onToggleQuestionnaire(q.id)}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault()
                    onToggleQuestionnaire(q.id)
                  }
                }}
              >
                <Typography sx={styles.rightQuestionnaireName}>
                  {q.name}
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                  <Box
                    sx={{
                      ...styles.rightQuestionnaireBadge,
                      backgroundColor: badgeColor,
                    }}
                  >
                    {q.score}%
                  </Box>
                  <Box sx={styles.rightQuestionnaireArrow}>
                    {isExpanded ? <ArrowDropUp sx={{ fontSize: '30px' }} /> : <ArrowDropDown sx={{ fontSize: '30px' }} />}
                  </Box>
                </Box>
              </Box>

              {/* Expanded content: sections list */}
              {isExpanded && (
                <Box sx={{
                  mb: '14px',
                  mx: '16px',
                  p: '8px 2px',
                  border: '1px solid #82828280',
                  borderTop: 'none',
                  borderRadius: '0 0 10px 10px',
                  overflow: 'hidden',
                  backgroundColor: '#fff',
                }}>
                  {[...q.sections].sort((a, b) =>
                    sortOrder === 'desc' ? b.score - a.score : a.score - b.score
                  ).map((section) => {
                    const indicatorColor = getScoreColor(section.score)
                    return (
                      <Box key={section.id} sx={styles.rightSectionRow}>
                        {/* Left column: 40% - Indicator + Name */}
                        <Box sx={styles.rightSectionLeftColumn}>
                          <Box
                            sx={{
                              ...styles.rightSectionIndicator,
                              backgroundColor: indicatorColor,
                            }}
                          />
                          <Typography sx={styles.rightSectionName}>
                            {section.name}
                          </Typography>
                        </Box>
                        {/* Right column: 60% - Track + Score */}
                        <Box sx={styles.rightSectionRightColumn}>
                          <Box sx={styles.rightSectionTrack}>
                            <Box
                              sx={{
                                ...styles.rightSectionFill,
                                width: `${section.score}%`,
                                backgroundColor: indicatorColor,
                              }}
                            />
                          </Box>
                          <Typography sx={styles.rightSectionScore}>
                            {section.score}%
                          </Typography>
                        </Box>
                      </Box>
                    )
                  })}
                </Box>
              )}
            </Box>
          )
        })}

            {questionnaires.length === 0 && (
              <Box sx={{ p: 2, textAlign: 'center' }}>
                <Typography sx={{ fontSize: '13px', color: '#9ca3af' }}>
                  {t('general.ninformationNotFound')}
                </Typography>
              </Box>
            )}
          </>
        )}
      </Box>

      {/* Pagination at the bottom */}
      {totalPagesRight > 1 && (
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 'auto', p: '12px 16px' }}>
          <Pagination
            page={pageRight}
            totalPages={totalPagesRight}
            onPageChange={onPageChangeRight}
            maxVisiblePages={2}
            styles={{
              paginationContainer: styles.paginationContainer,
              paginationButton: styles.paginationButton,
              paginationPage: styles.paginationPage,
              paginationPageActive: styles.paginationPageActive,
              paginationDisabled: styles.paginationDisabled,
            }}
          />
        </Box>
      )}
    </Box>
  )
}

const ProcessPerformancePanelMemo = memo(ProcessPerformancePanel)
export { ProcessPerformancePanelMemo as ProcessPerformancePanel }
